// SinglePagePrintComponent.jsx
import React, { useState, useEffect, useRef } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import he from 'he';

const SinglePagePrintComponent = () => {
    const [questionData, setQuestionData] = useState(null);
    const [quesPaperInfo, setQuesPaperInfo] = useState(null);
    const [status, setStatus] = useState('Upload a JSON file to get started');
    const [isLoading, setIsLoading] = useState(false);
    const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
    const [uploadedFileName, setUploadedFileName] = useState('');
    
    const fileInputRef = useRef(null);
    const shouldAutoPrint = useRef(false);

    const [printOptions, setPrintOptions] = useState({
        selectedSet: 'A',
        coverPage: false,
        twinColumn: true,
        watermark: true,
        roughWork: false
    });

    useEffect(() => {
        if (shouldAutoPrint.current && questionData && quesPaperInfo) {
            shouldAutoPrint.current = false;
            setTimeout(() => {
                handlePrint();
            }, 100);
        }
    }, [questionData, quesPaperInfo]);

    const renderToHTML = (text) => {
        if (!text) return '';

        const decoded = he.decode(text);

        const imagePlaceholders = [];
        let processedText = decoded.replace(/(imgsrc=[^ ]+)/g, (match) => {
            const placeholder = `__IMAGE_${imagePlaceholders.length}__`;
            imagePlaceholders.push(match);
            return placeholder;
        });

        processedText = processedText.replace(/\\\[([\s\S]*?)\\\]|\\\(([\s\S]*?)\\\)/g, (match, displayMath, inlineMath) => {
            const math = displayMath || inlineMath;
            const isDisplay = !!displayMath;

            try {
                return katex.renderToString(math, {
                    throwOnError: false,
                    displayMode: isDisplay
                });
            } catch (e) {
                console.warn('KaTeX error:', e, 'Math:', math);
                return `<span style="color: red;">[LaTeX Error]</span>`;
            }
        });

        processedText = processedText.replace(/__IMAGE_(\d+)__/g, (match, index) => {
            const imgSrc = imagePlaceholders[parseInt(index)];
            const url = imgSrc.replace('imgsrc=', '');
            return `<img src="${url}" alt="image" loading="eager" fetchpriority="high" style="width: auto; height: auto; min-width: 50px; min-height: 50px; max-width: 90%; vertical-align: middle; margin: 3px; object-fit: contain;" />`;
        });

        return processedText;
    };

    const getOptionLabel = (index, pattern) => {
        if (pattern === 1) return `${index + 1}.`;
        if (pattern === 2) return `${String.fromCharCode(97 + index)}.`;
        if (pattern === 3) {
            const roman = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x'];
            return roman[index] || `${index + 1}.`;
        }
        return `${index + 1}.`;
    };

    const transformMockData = (mockData) => {
        try {
            if (!mockData || !mockData.data) {
                throw new Error('Invalid data structure: missing mockData.data');
            }

            const subjectWiseDetails = mockData.data.subject_wise_details;

            if (!subjectWiseDetails || !Array.isArray(subjectWiseDetails)) {
                throw new Error('Invalid data structure: subject_wise_details is missing or not an array');
            }

            const sets = [{
                set_name: mockData.data.set_name || 'A',
                subjects: subjectWiseDetails.map(subject => {
                    if (!subject || !subject.section) {
                        console.warn('Skipping invalid subject:', subject);
                        return null;
                    }

                    return {
                        subject_name: subject.subject_name || 'Unknown Subject',
                        sections: (Array.isArray(subject.section) ? subject.section : [subject.section]).map(sec => {
                            if (!sec || !sec.questions) {
                                console.warn('Skipping invalid section:', sec);
                                return null;
                            }

                            return {
                                section_name: sec.section_name || 'A',
                                section_instruction: sec.section_instructions || null,
                                questions: (Array.isArray(sec.questions) ? sec.questions : []).map(q => {
                                    if (!q || !q.options) {
                                        console.warn('Skipping invalid question:', q);
                                        return null;
                                    }

                                    return {
                                        ques_text: q.question_html || '',
                                        set_ques_position: q.question_no || 0,
                                        options: (Array.isArray(q.options) ? q.options : []).map(opt => ({
                                            option_text: opt.option_text || '',
                                            set_option_position: opt.option_no || 0
                                        }))
                                    };
                                }).filter(q => q !== null)
                            };
                        }).filter(sec => sec !== null)
                    };
                }).filter(subj => subj !== null)
            }];

            const quesPaperInfo = {
                header: {
                    test_name: mockData.data.test_schedule_code || 'Sample Test',
                    date: new Date().toLocaleDateString()
                },
                option_naming_pattern: 2
            };

            return { sets, quesPaperInfo };
        } catch (error) {
            console.error('Error transforming mock data:', error);
            throw error;
        }
    };

    // ✅ Handle file upload
    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!file.name.endsWith('.json')) {
            alert('Please upload a valid JSON file');
            return;
        }

        setUploadedFileName(file.name);
        setIsLoading(true);
        setStatus('Reading file...');

        const reader = new FileReader();
        
        reader.onload = (e) => {
            try {
                const jsonData = JSON.parse(e.target.result);
                const { sets, quesPaperInfo } = transformMockData(jsonData);
                
                setQuestionData(sets);
                setQuesPaperInfo(quesPaperInfo);

                const totalQuestions = sets[0]?.subjects?.reduce((sum, subj) =>
                    sum + subj.sections.reduce((s, sec) => s + sec.questions.length, 0), 0) || 0;

                setStatus(`✅ File loaded successfully! Total questions: ${totalQuestions}`);
                setIsLoading(false);
            } catch (error) {
                console.error('Error parsing JSON:', error);
                setStatus(`❌ Error: ${error.message}`);
                setIsLoading(false);
                alert(`Failed to parse JSON file: ${error.message}`);
            }
        };

        reader.onerror = () => {
            setStatus('❌ Error reading file');
            setIsLoading(false);
            alert('Failed to read file');
        };

        reader.readAsText(file);
    };

    const generateRoughWorkHTML = () => {
        return `
      <div class="rough-work-page">
        <h2 class="rough-work-title">ROUGH WORK</h2>
        <div class="rough-work-lines"></div>
      </div>
      <div class="rough-work-page">
        <h2 class="rough-work-title">ROUGH WORK</h2>
        <div class="rough-work-lines"></div>
      </div>
    `;
    };

    const generateSingleColumnPrintHTML = () => {
        if (!questionData || questionData.length === 0 || !quesPaperInfo) {
            return '<p>No data available to print</p>';
        }

        let html = '';
        html += '<div class="print-container single-column">';
        const set = questionData[0];
        let questionCounter = 0;

        set.subjects.forEach((subject, subjectIndex) => {
            if (subjectIndex > 0) {
                html += `<div class="subject-divider"></div>`;
            }

            subject.sections.forEach(section => {
                html += `<p class="section-heading">${subject.subject_name} - Set ${set.set_name} - Section ${section.section_name}</p>`;

                if (section.section_instruction?.trim()) {
                    html += `<p class="section-instruction"><strong>Instruction:</strong> ${renderToHTML(section.section_instruction)}</p>`;
                }

                const questions = [...section.questions].sort((a, b) => a.set_ques_position - b.set_ques_position);

                questions.forEach(question => {
                    questionCounter++;

                    html += `
            <div class="question-block">
              <p class="question-text"><strong>${questionCounter}.</strong> ${renderToHTML(question.ques_text)}</p>
              <div class="options">
          `;

                    const options = [...question.options].sort((a, b) => a.set_option_position - b.set_option_position);

                    options.forEach((option, idx) => {
                        html += `
              <p class="option-text">
                <span>${getOptionLabel(idx, quesPaperInfo.option_naming_pattern)}</span> 
                ${renderToHTML(option.option_text)}
              </p>
            `;
                    });

                    html += `
              </div>
            </div>
          `;
                });
            });
        });

        html += '</div>';

        if (printOptions.roughWork) {
            html += generateRoughWorkHTML();
        }

        return html;
    };

    const generateTwinColumnPrintHTML = () => {
        if (!questionData || questionData.length === 0 || !quesPaperInfo) {
            return '<p>No data available to print</p>';
        }

        let html = '';
        html += '<div class="print-container twin-column">';
        const set = questionData[0];
        let questionCounter = 0;

        set.subjects.forEach((subject, subjectIndex) => {
            if (subjectIndex > 0) {
                html += `<div class="subject-divider"></div>`;
            }

            subject.sections.forEach(section => {
                html += `<div class="section-header-wrapper">`;
                html += `<p class="section-heading">${subject.subject_name} - Set ${set.set_name} - Section ${section.section_name}</p>`;

                if (section.section_instruction?.trim()) {
                    html += `<p class="section-instruction"><strong>Instruction:</strong> ${renderToHTML(section.section_instruction)}</p>`;
                }
                html += `</div>`;

                html += `<div class="columned-content">`;

                const questions = [...section.questions].sort((a, b) => a.set_ques_position - b.set_ques_position);

                questions.forEach(question => {
                    questionCounter++;

                    html += `<p class="question-text"><strong>${questionCounter}.</strong> ${renderToHTML(question.ques_text)}</p>`;

                    const options = [...question.options].sort((a, b) => a.set_option_position - b.set_option_position);

                    options.forEach((option, idx) => {
                        html += `
            <p class="option-text">
              <span>${getOptionLabel(idx, quesPaperInfo.option_naming_pattern)}</span> 
              ${renderToHTML(option.option_text)}
            </p>
          `;
                    });

                    html += `<div class="question-spacer"></div>`;
                });

                html += `</div>`;
            });
        });

        html += '</div>';

        if (printOptions.roughWork) {
            html += generateRoughWorkHTML();
        }

        return html;
    };

    const configureBrowserHeader = () => {
        if (questionData && quesPaperInfo) {
            const rawDate = quesPaperInfo.header?.date || '23 December, 2025';
            const date = rawDate.split(',').slice(0, 2).join(',').trim() || rawDate.split(' ').slice(0, 3).join(' ');
            const testName = quesPaperInfo.header?.test_name || 'Test';
            const subjects = questionData[0]?.subjects?.map(s => s.subject_name).join(' | ') || 'Subject';

            document.title = `${date}                    ${testName}                    ${subjects}`;
        }
    };

    const preloadImages = (html) => {
        return new Promise((resolve) => {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;

            const images = tempDiv.getElementsByTagName('img');
            const imageUrls = Array.from(images).map(img => img.src);

            if (imageUrls.length === 0) {
                resolve();
                return;
            }

            let loadedCount = 0;
            const totalImages = imageUrls.length;

            imageUrls.forEach(url => {
                const img = new Image();
                img.onload = img.onerror = () => {
                    loadedCount++;
                    if (loadedCount === totalImages) {
                        resolve();
                    }
                };
                img.src = url;
            });

            setTimeout(() => {
                console.warn(`Only ${loadedCount}/${totalImages} images loaded`);
                resolve();
            }, 10000);
        });
    };

    const handlePrint = async () => {
        if (!questionData || !quesPaperInfo) {
            console.warn('Print attempted but data not ready');
            return;
        }

        setStatus('Preparing print... Loading images...');
        configureBrowserHeader();

        const printContent = printOptions.twinColumn
            ? generateTwinColumnPrintHTML()
            : generateSingleColumnPrintHTML();

        await preloadImages(printContent);
        setStatus('Images loaded. Opening print dialog...');

        const printWindow = document.createElement('div');
        printWindow.id = 'printSectionDynamic';
        printWindow.innerHTML = printContent;

        const existingPrint = document.getElementById('printSectionDynamic');
        if (existingPrint) {
            document.body.removeChild(existingPrint);
        }

        document.body.appendChild(printWindow);

        setTimeout(() => {
            window.print();
            setIsPrintModalOpen(false);
            setTimeout(() => {
                const element = document.getElementById('printSectionDynamic');
                if (element) {
                    document.body.removeChild(element);
                }
            }, 1000);
        }, 300);
    };

    const openPrintModal = () => {
        if (!questionData) {
            alert('Please upload a JSON file first');
            return;
        }
        setIsPrintModalOpen(true);
    };

    return (
        <>
            <div style={styles.pageContainer}>
                <div style={styles.mainCard}>
                    {/* Header */}
                    <div style={styles.cardHeader}>
                        <h1 style={styles.mainTitle}>📄 Question Paper Printer</h1>
                        <p style={styles.subtitle}>Upload your question paper JSON and print with custom options</p>
                    </div>

                    {/* File Upload Section */}
                    <div style={styles.uploadSection}>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".json"
                            onChange={handleFileUpload}
                            style={styles.fileInput}
                            id="fileInput"
                        />
                        <label htmlFor="fileInput" style={styles.uploadButton}>
                            <span style={styles.uploadIcon}>📁</span>
                            {uploadedFileName || 'Choose JSON File'}
                        </label>
                        
                        {uploadedFileName && (
                            <button
                                onClick={() => {
                                    setUploadedFileName('');
                                    setQuestionData(null);
                                    setQuesPaperInfo(null);
                                    setStatus('Upload a JSON file to get started');
                                    if (fileInputRef.current) {
                                        fileInputRef.current.value = '';
                                    }
                                }}
                                style={styles.clearButton}
                            >
                                ✕ Clear
                            </button>
                        )}
                    </div>

                    {/* Status */}
                    <div style={{
                        ...styles.statusBox,
                        ...(status.includes('✅') ? styles.statusSuccess : {}),
                        ...(status.includes('❌') ? styles.statusError : {})
                    }}>
                        {status}
                    </div>

                    {/* Print Button */}
                    {questionData && (
                        <button
                            onClick={openPrintModal}
                            style={styles.printButton}
                            disabled={isLoading}
                        >
                            <span style={styles.printIcon}>🖨️</span>
                            Open Print Preview
                        </button>
                    )}
                </div>
            </div>

            {/* Print Modal */}
            {isPrintModalOpen && (
                <div style={styles.overlay} onClick={() => setIsPrintModalOpen(false)}>
                    <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <h2 style={styles.modalTitle}>Question Paper Print Preview</h2>
                            <button style={styles.closeButton} onClick={() => setIsPrintModalOpen(false)}>
                                ✕
                            </button>
                        </div>

                        <div style={styles.modalBody}>
                            <div style={styles.setSelectionContainer}>
                                <h3 style={styles.sectionTitle}>Select Set</h3>
                                <div style={styles.radioGroup}>
                                    {['A', 'B', 'C', 'D', 'All'].map(set => (
                                        <label key={set} style={styles.radioLabel}>
                                            <input
                                                type="radio"
                                                name="set"
                                                value={set}
                                                checked={printOptions.selectedSet === set}
                                                onChange={(e) => setPrintOptions({ ...printOptions, selectedSet: e.target.value })}
                                                style={styles.radio}
                                            />
                                            <span style={styles.radioText}>{set}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div style={styles.optionsContainer}>
                                <h3 style={styles.sectionTitle}>Print Options</h3>

                                <div style={styles.checkboxGroup}>
                                    <label style={styles.checkboxLabel}>
                                        <input
                                            type="checkbox"
                                            checked={printOptions.twinColumn}
                                            onChange={(e) => setPrintOptions({ ...printOptions, twinColumn: e.target.checked })}
                                            style={styles.checkbox}
                                        />
                                        Twin Column
                                    </label>

                                    <label style={styles.checkboxLabel}>
                                        <input
                                            type="checkbox"
                                            checked={printOptions.watermark}
                                            onChange={(e) => setPrintOptions({ ...printOptions, watermark: e.target.checked })}
                                            style={styles.checkbox}
                                        />
                                        Watermark
                                    </label>

                                    <label style={styles.checkboxLabel}>
                                        <input
                                            type="checkbox"
                                            checked={printOptions.roughWork}
                                            onChange={(e) => setPrintOptions({ ...printOptions, roughWork: e.target.checked })}
                                            style={styles.checkbox}
                                        />
                                        Rough Work Pages
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div style={styles.modalFooter}>
                            <button onClick={handlePrint} style={styles.modalPrintButton}>
                                <span style={styles.printIcon}>🖨️</span> PRINT
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Print Styles */}
            <style>{`
@media print {
  body * { visibility: hidden !important; }
  #printSectionDynamic, #printSectionDynamic * { visibility: visible !important; }
  #printSectionDynamic { 
    position: absolute !important; 
    left: 0; 
    top: 0; 
    width: 100%; 
  }
  
  ${printOptions.watermark ? `
  #printSectionDynamic::before {
    content: "";
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 300px;
    height: 300px;
    background-image: url('http://localhost:3000/client_logos/dsc_bpl.png');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    opacity: 0.8;
    z-index: 1;
    pointer-events: none;
  }
  ` : ''}
}

@page { 
  size: A4; 
  margin: 15mm 10mm;
}

.rough-work-page {
  width: 100%;
  height: 100vh;
  page-break-after: always;
  padding: 40px;
}

.rough-work-title {
  font-family: 'Times New Roman', serif;
  font-size: 24pt;
  font-weight: bold;
  text-align: center;
  margin-bottom: 30px;
  border-bottom: 2px solid #000;
  padding-bottom: 10px;
}

.rough-work-lines {
  height: calc(100% - 80px);
  background-image: repeating-linear-gradient(
    transparent,
    transparent 35px,
    #ccc 35px,
    #ccc 36px
  );
}

.print-container {
  width: 100%;
  font-family: 'Times New Roman', serif;
  font-size: 11pt;
  line-height: 1.4;
  position: relative;
  z-index: 2;
}

.section-header-wrapper {
  column-span: all;
  -webkit-column-span: all;
  page-break-after: avoid;
}

.section-heading {
  font-size: 13pt;
  font-weight: 500;
  margin: 10px 0 5px 0;
  page-break-after: avoid;
}

.section-instruction {
  margin: 0 0 8px 0;
  page-break-after: avoid;
}

.subject-divider {
  page-break-before: always;
  column-span: all;
  -webkit-column-span: all;
}

.single-column .question-block {
  margin-bottom: 6px;
  page-break-inside: avoid;
  break-inside: avoid;
}

.single-column .question-text {
  margin: 0 0 2px 0;
  line-height: 1.3;
  page-break-after: avoid;
}

.single-column .options {
  margin-left: 20px;
  margin-bottom: 0;
}

.single-column .option-text {
  margin: 1px 0;
  font-size: 10.5pt;
  line-height: 1.2;
}

.twin-column .columned-content {
  column-count: 2;
  -webkit-column-count: 2;
  -moz-column-count: 2;
  column-gap: 20px;
  -webkit-column-gap: 20px;
  -moz-column-gap: 20px;
  column-rule: 2px solid #000;
  -webkit-column-rule: 2px solid #000;
  -moz-column-rule: 2px solid #000;
  column-fill: auto;
  -webkit-column-fill: auto;
  -moz-column-fill: auto;
}

.twin-column .question-text {
  margin: 6px 0 2px 0;
  line-height: 1.3;
  font-size: 10.5pt;
  page-break-after: avoid;
  break-after: avoid-column;
  orphans: 2;
  widows: 2;
  page-break-inside: avoid;
  break-inside: avoid;
}

.twin-column .option-text {
  margin: 1px 0 1px 15px;
  font-size: 10pt;
  line-height: 1.2;
  orphans: 2;
  widows: 2;
}

.twin-column .question-spacer {
  height: 4px;
  margin: 0;
  padding: 0;
}

.question-block img,
.options img,
.question-text img,
.option-text img {
  width: auto !important;
  height: auto !important;
  min-width: 40px !important;
  min-height: 40px !important;
  max-width: 85% !important;
  margin: 2px !important;
  vertical-align: middle !important;
  object-fit: contain !important;
  page-break-inside: avoid;
}

.twin-column img {
  max-width: 75% !important;
}

p {
  orphans: 2;
  widows: 2;
}
`}</style>
        </>
    );
};

const styles = {
    pageContainer: {
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#f3f4f6',
        padding: '20px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    mainCard: {
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '40px',
        maxWidth: '600px',
        width: '100%',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
    },
    cardHeader: {
        textAlign: 'center',
        marginBottom: '30px'
    },
    mainTitle: {
        margin: '0 0 10px 0',
        fontSize: '32px',
        fontWeight: '700',
        color: '#1f2937'
    },
    subtitle: {
        margin: 0,
        fontSize: '14px',
        color: '#6b7280'
    },
    uploadSection: {
        display: 'flex',
        gap: '10px',
        marginBottom: '20px',
        alignItems: 'center',
        flexWrap: 'wrap'
    },
    fileInput: {
        display: 'none'
    },
    uploadButton: {
        flex: 1,
        padding: '16px 24px',
        fontSize: '16px',
        fontWeight: '600',
        color: 'white',
        backgroundColor: '#3b82f6',
        border: 'none',
        borderRadius: '12px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
        transition: 'all 0.3s ease',
        minWidth: '200px'
    },
    uploadIcon: {
        fontSize: '20px'
    },
    clearButton: {
        padding: '16px 24px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#ef4444',
        backgroundColor: 'white',
        border: '2px solid #ef4444',
        borderRadius: '12px',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    statusBox: {
        padding: '16px',
        backgroundColor: '#f9fafb',
        border: '2px solid #e5e7eb',
        borderRadius: '12px',
        marginBottom: '20px',
        fontSize: '14px',
        color: '#374151',
        textAlign: 'center',
        fontWeight: '500'
    },
    statusSuccess: {
        backgroundColor: '#f0fdf4',
        borderColor: '#86efac',
        color: '#166534'
    },
    statusError: {
        backgroundColor: '#fef2f2',
        borderColor: '#fca5a5',
        color: '#991b1b'
    },
    printButton: {
        width: '100%',
        padding: '16px 24px',
        fontSize: '16px',
        fontWeight: '600',
        color: 'white',
        backgroundColor: '#10b981',
        border: 'none',
        borderRadius: '12px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
        transition: 'all 0.3s ease',
    },
    printIcon: {
        fontSize: '20px'
    },
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
        padding: '20px'
    },
    modal: {
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '90%',
        maxWidth: '700px',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
        overflow: 'hidden'
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px 24px',
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: '#f8f9fa'
    },
    modalTitle: {
        margin: 0,
        fontSize: '18px',
        fontWeight: '600',
        color: '#333'
    },
    closeButton: {
        background: 'none',
        border: 'none',
        fontSize: '24px',
        cursor: 'pointer',
        color: '#666',
        padding: '0',
        width: '36px',
        height: '36px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '6px',
        transition: 'all 0.2s'
    },
    modalBody: {
        padding: '25px',
        overflowY: 'auto',
        flex: 1,
        backgroundColor: '#f8f9fa'
    },
    setSelectionContainer: {
        marginBottom: '20px'
    },
    sectionTitle: {
        margin: '0 0 15px 0',
        fontSize: '16px',
        fontWeight: '600',
        color: '#333'
    },
    radioGroup: {
        display: 'flex',
        gap: '10px',
        flexWrap: 'wrap',
    },
    radioLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        cursor: 'pointer',
        fontSize: '14px',
        padding: '10px 20px',
        backgroundColor: 'white',
        border: '2px solid #e5e7eb',
        borderRadius: '8px',
        transition: 'all 0.2s',
        fontWeight: '500',
        flex: '1',
        justifyContent: 'center',
        minWidth: '80px'
    },
    radio: {
        cursor: 'pointer',
        width: '18px',
        height: '18px',
        accentColor: '#3b82f6'
    },
    radioText: {
        userSelect: 'none'
    },
    optionsContainer: {
        marginTop: '20px'
    },
    checkboxGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
    },
    checkboxLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        cursor: 'pointer',
        fontSize: '14px',
        padding: '12px 16px',
        backgroundColor: 'white',
        border: '2px solid #e5e7eb',
        borderRadius: '8px',
        transition: 'all 0.2s',
        fontWeight: '500'
    },
    checkbox: {
        cursor: 'pointer',
        width: '20px',
        height: '20px',
        accentColor: '#3b82f6'
    },
    modalFooter: {
        display: 'flex',
        justifyContent: 'flex-end',
        padding: '20px 24px',
        borderTop: '1px solid #e0e0e0',
        backgroundColor: '#f8f9fa'
    },
    modalPrintButton: {
        padding: '14px 32px',
        fontSize: '16px',
        backgroundColor: '#10b981',
        color: 'white',
        border: 'none',
        cursor: 'pointer',
        borderRadius: '8px',
        fontWeight: '600',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
        transition: 'all 0.2s',
    }
};

export default SinglePagePrintComponent;
